import {
  Box,
  TextField,
  Typography,
  Grid,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Add, ArrowBack, Cancel, CheckCircleOutline, Close, Comment, Delete, PaymentsOutlined } from "@mui/icons-material";
import Button from "../../common/Button";
import CustomDatePicker from "../../common/CustomDatePicker";
import { useForm } from "react-hook-form";
import CustomItemList, { Column } from "../../common/CustomItemList";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomIconButton from "../../common/CustomIconButton";
import { useNavigation } from "../../../hooks/useNavigation";
import { fetchManufacturersList, setSingleAlertObj, SingleAlertInfo, useCommonReducer } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { getInvoiceByManufacturerAndPractice, resetInvoicesByManufacturerAndPractice, useInvoiceSlice } from "../../../redux/slice/invoice/InvoiceSlice";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { recordMultiplePayments } from "../../../redux/slice/payment/paymentSlice";
import dayjs from "dayjs";
import OptionsPopup from "../../common/OptionsPopup";
import { priceFormatter } from "../../../utils/Utility";
import { getPracticesByUserRole, usePracticeSlice } from "../../../redux/slice/practiceSlice";
import { fetchAllUserRoles, UserRole } from "../../../redux/slice/userSlice";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface PaymentRow {
  practiceName: string;
  invoiceId: number;
  manufacturerId: number;
  mfgInvoiceNumber: string;
  invReferenceId?: string | null;
  purchaseAndBill?: string | null;
  mfgInvoiceDate: string;
  practiceId: number;
  invoiceAmount: string;
  paidAmount: string;
  paymentDate: string;
  paymentComments: string;
  remainingAmount: string;
}

type Option = {
  label: string;
  value: number;
};

const RecordPayment = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    paperSx,
    headerSx,
    closeIconSx,
    bodySx,
    footerSx,
    primaryBtnSx,
    cancelBtnSx,
    dropdownWrapSx,
    datepickerWrapSx,
    inputFieldSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const { navigate } = useNavigation();

  const [rows, setRows] = useState<PaymentRow[]>([]);
  // const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState<number>(0);
  const [selectedPractice, setSelectedPractice] = useState<number>(0);
  const [commentIndex, setCommentIndex] = useState<number | null>(null);
  const [commentText, setCommentText] = useState<string>("");
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const { control, setValue, getValues, reset, handleSubmit, trigger, watch, formState: { errors } } = useForm();
  const { manufacturerInfoList } = useCommonReducer();
  const dispatch = useAppDispatch();
  const { invoicesByManufacturerAndPractice } = useInvoiceSlice();
  const[confirmOpen, setConfirmOpen] = useState (false);
  const [autocompleteKey, setAutocompleteKey] = useState<string | null>(null);
  const { practiceByUserRole } = usePracticeSlice();
  const { userInfo } = useAuthSlice();

  useEffect(() => {
    dispatch(fetchManufacturersList());
    fetchPracticeListByUserRole();
  }, [])

    const fetchPracticeListByUserRole = async () => {
      const response = await dispatch(fetchAllUserRoles());
      if (response?.meta?.requestStatus === "fulfilled") {
        if (response?.payload) {
          const useRoles = response.payload;
          const role = useRoles?.find(
            (role: UserRole) => role.roleName === userInfo.userRole
          );
          if (role) {
            dispatch(getPracticesByUserRole(true));
          }
        }
      }
    };

  const manufacturersList = useMemo(() => {
    let options: { label: string, value: any }[] = [];
    manufacturerInfoList?.map((option) => {
      options.push({ label: option.manufacturerName, value: option.manufacturerId })
    });
    return options;
  }, [manufacturerInfoList]);

  const practiceList = useMemo(() => {
    let options: { label: string; value: any }[] = [];
    if (practiceByUserRole) {
      if (practiceByUserRole?.length > 1) {
        practiceByUserRole.map((item) => {
          options.push({ label: item.practiceName, value: item.practiceId });
        });
      } else {
        setValue("practiceIds", [Number(practiceByUserRole[0]?.practiceId)]);
        practiceByUserRole.map((item) => {
          options.push({ label: item.practiceName, value: item.practiceId });
        });
      }
    }
    return options;
  }, [practiceByUserRole]);

  const handleChange = <K extends keyof PaymentRow>(
    index: number,
    field: K,
    value: PaymentRow[K]
  ) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
  };

  const clearControllerOnMfgChange = (e: any) => {
    setValue('invoice', null);
    // setSelectedInvoice(null);
    // setRows([]);
    // setAutocompleteKey(`autocomplete_${e.value}` ); // Force component re-render
  };

  const handleManufacturerChange = (e: any) => {
    if (e) {
      setSelectedManufacturer(e.value)
      // dispatch(getInvoiceByManufacturer({manufacturerId: e.value}));
      clearControllerOnMfgChange(e);
    } else {
      setSelectedManufacturer(0);
    }
  };

  const handlePracticeChange = (e: any) => {
    if (e) {
      setSelectedPractice(e.value)
      setAutocompleteKey(`autocomplete_${e.value}` ); // Force component re-render
      clearControllerOnMfgChange(e);
    } 
    else {
      setSelectedPractice(0);
    }
  }

  const handleRemove = (index: number) => {
    const rowToRemove = rows.find(row => row.invoiceId === index);

    if (rowToRemove) {
      setValue(`amount_${rowToRemove.invoiceId}`, '');
      setValue(`paymentDate_${rowToRemove.invoiceId}`, null);
    }

    const updated = rows.filter(row => row.invoiceId !== index);
    setRows(updated);
  };

  const handleAddInvoice = () => {
    if (getValues('invoice') && invoicesByManufacturerAndPractice && invoicesByManufacturerAndPractice.length > 0) {
      const foundInvoice = invoicesByManufacturerAndPractice.find(
        invoice => invoice.invoiceId === getValues('invoice')
      );

      if (foundInvoice) {
        const existingInvoice = rows.find(
          row => row.invoiceId === foundInvoice.invoiceId
        );

        if (existingInvoice) {
          let errObj: SingleAlertInfo = {
            message: "This invoice has already been added to the payment list.",
            alertType: "warning"
          };
          dispatch(setSingleAlertObj(errObj));
          return;
        }

        setValue(`amount_${foundInvoice.invoiceId}`, '');
        setValue(`paymentDate_${foundInvoice.invoiceId}`, null);

        const newRow: PaymentRow = {
          ...foundInvoice,
          paidAmount: "",
          paymentDate: "",
          paymentComments: "",
          invoiceAmount: priceFormatter(Number(foundInvoice.invoiceAmount)),
          remainingAmount: priceFormatter(Number(foundInvoice.remainingAmount)),
          invReferenceId: foundInvoice.purchaseAndBill === "Y" ? (foundInvoice.invReferenceId || "") : "",
          purchaseAndBill: foundInvoice.purchaseAndBill,
        };
        setRows((prev) => [...prev, newRow]);
      }
    }
  };

  const handleRecordPayment = async () => {
    const response = await dispatch(recordMultiplePayments(rows))
    if (response?.payload?.message === "SUCCESS") {
      let errObj: SingleAlertInfo = {
        message: "Payment(s) recorded successfully.",
        alertType: "success"
      };
      dispatch(setSingleAlertObj(errObj));
      navigate("/payment_list")
    }
    setConfirmOpen(false);
  };

  useEffect(() => {
  if (selectedManufacturer > 0 || selectedPractice > 0) {
      dispatch(getInvoiceByManufacturerAndPractice({ manufacturerId: selectedManufacturer, practiceId: selectedPractice, skipLoader: true })).unwrap();
    } else {
      dispatch(resetInvoicesByManufacturerAndPractice());
    }
  }, [selectedManufacturer, selectedPractice])

  const fetchInvoicesByManufacturer = async (query: string, type?: string) => {
    if (selectedManufacturer > 0 || selectedPractice > 0) {
      const response = await dispatch(getInvoiceByManufacturerAndPractice({ manufacturerId: selectedManufacturer, practiceId: selectedPractice, skipLoader: query.length > 0 ? true : false })).unwrap();
    };
  };

  const invoiceOptions = useMemo(() => {
    const options: Option[] = [];
    invoicesByManufacturerAndPractice?.forEach(invoice => {
      const option: Option = {
        value: invoice.invoiceId,
        label: invoice.mfgInvoiceNumber
      };
      options.push(option)
    });
    return options;
  }, [invoicesByManufacturerAndPractice]);

  const transformInvoicesResponse = (res: any): any[] => {
    return (res?.data || []).map((item: any) => ({
      value: item.invoiceId,
      label: item.mfgInvoiceNumber,
    }));
  }

  const totalCash = rows.reduce(
    (sum, r) => sum + (parseFloat(r.paidAmount) || 0),
    0
  );

  const onSubmit = async () => {
    if (rows.length > 0) {
      setConfirmOpen(true)
    } else {
      let errObj: SingleAlertInfo = {
        message: "Please add at least one payment record.",
        alertType: "warning"
      };
      dispatch(setSingleAlertObj(errObj));
    }
  }

  const validateAmountOnBlur = async (row: any) => {
    await trigger(`amount_${row.invoiceId}`);
  };

  const columns: Column[] = [
    { id: "practiceName", label: "Practice" },
    { id: "mfgInvoiceNumber", label: "INV ID" },
    ...(rows.some((row) => row.purchaseAndBill === "Y")
      ? [{ id: "invReferenceId", label: "Invoice Reference Id" }]
      : []),
    { id: "mfgInvoiceDate", label: "INV Date" },
    { id: "invoiceAmount", label: "INV Amt" },
    { id: "remainingAmount", label: "Remaining Amt" },
    { id: "amount", label: "Cash Applied" },
    { id: "paymentDate", label: "Payment Date" },
    { id: "commentAndRemove", label: "Comment/Remove", minWidth: 150, align: "center" },
  ];

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.9, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}
      {required && (
        <Box component="span" sx={{ color: mTokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const itemListWrapSx = {
    borderRadius: "12px",
    border: `1px solid ${mTokens.border}`,
    overflow: "hidden" as const,
    backgroundColor: mTokens.bg,
    "& .MuiList-root": {
      bgcolor: "transparent !important",
      borderRadius: "0 !important",
    },
    "& .MuiListItem-root": {
      backgroundColor: "transparent !important",
      borderBottom: `1px solid ${mTokens.border}`,
    },
    "& .MuiListItem-root:last-of-type": {
      borderBottom: "none",
    },
    "& .MuiListItemButton-root": {
      borderRadius: "0 !important",
      "&:hover": {
        backgroundColor: isDarkModal ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.05)",
      },
    },
    "& .MuiListItem-root:first-of-type": {
      backgroundColor: `${mTokens.headerFooterBg} !important`,
    },
    "& .MuiListItem-root:first-of-type .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiListItem-root:not(:first-of-type) .MuiTypography-root": {
      color: mTokens.textPrimary,
      fontSize: "0.84rem",
    },
  };

  const displayRows = rows.map((row, index) => ({
    ...row,
    amount: (
      <Box sx={inputFieldSx(!!errors[`amount_${row.invoiceId}`])}>
        <CustomCurrencyField
          controllerName={`amount_${row.invoiceId}`}
          control={control}
          fullWidth
          size="small"
          label=""
          externalValue={row.paidAmount}
          onChange={
            ((event: any, vals: any) =>
              handleChange(index, "paidAmount", vals?.floatValue ?? 0)
            ) as unknown as React.ChangeEventHandler<HTMLInputElement>
          }
          placeholder="Amount"
          rules={{
            required: "Amount is required.",
            validate: (value: number) => {
              const remainingAmount = parseFloat(row.remainingAmount.replace(/[$,]/g, ''));
              if (value > remainingAmount) {
                return "Paid amount cannot exceed remaining invoice amount.";
              }
              return true;
            }
          }}
          onBlur={() => validateAmountOnBlur(row)}
        />
      </Box>
    ),
    paymentDate: (
      <Box sx={datepickerWrapSx(!!errors[`paymentDate_${row.invoiceId}`])}>
        <CustomDatePicker
          controllerName={`paymentDate_${row.invoiceId}`}
          control={control}
          label=""
          size="small"
          onChange={(newDate) => {
            const formattedDate = newDate ? dayjs(newDate).format('MM/DD/YYYY') : '';
            handleChange(index, "paymentDate", formattedDate);
          }}
          fullWidth
          rules={{
            required: "Payment Date is required.",
            validate: (value:any) => {
              if (!value || !row.mfgInvoiceDate) return true;

              // const paymentDate = dayjs(value, 'MM/DD/YYYY');
              // const mfgDate = dayjs(row.mfgInvoiceDate, 'MM/DD/YYYY');

              // if (paymentDate.isBefore(mfgDate)) {
              //   return "Payment date cannot be before the invoice date.";
              // }

              return true;
            }
          }}
        />
      </Box>
    ),
    commentAndRemove: (
      <>
        <CustomIconButton
          color={row.paymentComments ? "success" : "info"}
          size="small"
          variant="outlined"
          showTooltip={!!row.paymentComments}
          tooltipMessage={row.paymentComments || ""}
          tooltipPlacement="top"
          onClick={() => {
            setCommentIndex(index);
            setCommentText(row.paymentComments || "");
            setOpenCommentDialog(true);
          }}
        >
          <Comment sx={{ color: row.paymentComments ? mTokens.accent : "#42A5F5" }} fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="warning"
          size="small"
          variant="outlined"
          tooltipMessage="Remove"
          tooltipPlacement="top"
          onClick={() => handleRemove(row.invoiceId)}
        >
          <Delete sx={{ color: mTokens.error }} fontSize="small" />
        </CustomIconButton>
      </>
    ),
  }));

  return (
    <Box
      sx={{
        mx: { xs: "10px", sm: "20px" },
        mt: "12px",
        pb: "72px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
          <IconButton
            onClick={() => navigate("/payment_list")}
            size="small"
            aria-label="Back"
            sx={{
              mt: 0.4,
              border: `1px solid ${mTokens.border}`,
              borderRadius: "10px",
              width: 34,
              height: 34,
              color: mTokens.textSecondary,
              backgroundColor: mTokens.bg,
              flexShrink: 0,
              "&:hover": { borderColor: mTokens.accent, color: mTokens.accent, backgroundColor: `${mTokens.accent}0d` },
            }}
          >
            <ArrowBack sx={{ fontSize: "1.1rem" }} />
          </IconButton>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
              Payment / Record Payments
            </Typography>
            <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
              Record Payments
            </Typography>
            {/* <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
              Select invoices and record one or more payments.
            </Typography> */}
          </Box>
        </Box>

        <Box sx={cardSx} data-testid="record-payment-card">
          <SectionHeader
            icon={<PaymentsOutlined fontSize="small" />}
            title="Payment Selection"
            subtitle="Choose a manufacturer, practice, and invoice, then add it to the table."
          />
          <Box sx={{ px: { xs: 2, md: 2.8 }, pt: 2.2, pb: 3.2 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={3}>
                <FieldLabel label="Manufacturer" />
                <Box sx={dropdownWrapSx()}>
                  <CustomDropdownField
                    name="manufacturer"
                    placeholderText="Select manufacturer"
                    control={control}
                    label=""
                    size="small"
                    options={manufacturersList}
                    onChangeSelect={handleManufacturerChange}
                    disabled={rows?.length > 0}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <FieldLabel label="Practice" />
                <Box sx={dropdownWrapSx()}>
                  <CustomDropdownField
                    name="practiceIds"
                    placeholderText="Select practice"
                    control={control}
                    label=""
                    size="small"
                    options={practiceList}
                    onChangeSelect={handlePracticeChange}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                {/* <CustomAutocompleteField
                  key={autocompleteKey}
                  name="invoice"
                  control={control}
                  label="Invoice"
                  fetchFunction={fetchInvoicesByManufacturer}
                  placeholderText={"Make sure to select a manufacturer to see suggestions"}
                  transformResponse={transformInvoicesResponse}
                  rules={{
                    required: rows.length === 0 ? "Invoice is required." : false
                    }}
                    onChangeSelect={(selected) =>
                      setSelectedInvoice(selected && !Array.isArray(selected) ? selected.value.toString() : null)
                    }
                    isMultiple={false}
                  /> */}
                <FieldLabel label="Invoice" required />
                <Box sx={dropdownWrapSx(!!errors.invoice)}>
                  <CustomDropdownField
                    name="invoice"
                    control={control}
                    label=""
                    size="small"
                    rules={{
                      required: rows.length === 0 ? "Invoice is required." : false
                    }}
                    placeholderText={"Make sure to select a manufacturer to see suggestions"}
                    options={invoiceOptions}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  variant="contained"
                  color="success"
                  removeUpperCase
                  startIcon={<Add />}
                  onClick={handleAddInvoice}
                  fullWidth
                  sx={{
                    ...primaryBtnSx,
                    height: 42,
                    px: 2.4,
                    whiteSpace: "nowrap",
                    "& .MuiTypography-root": { color: "#ffffff !important" },
                    "& .MuiButton-startIcon": { color: "#ffffff" },
                  }}
                >
                  Add to Table
                </Button>
              </Grid>
            </Grid>

            <Box mt={2.5} sx={itemListWrapSx}>
              <CustomItemList columns={columns} rows={displayRows} maxHeight={window.innerHeight * 0.6} uniqueValue="invoiceId" />
            </Box>

            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              mt={2.2}
              sx={{
                px: 1.5,
                py: 1.2,
                borderRadius: "12px",
                border: `1px solid ${mTokens.border}`,
                backgroundColor: mTokens.headerFooterBg,
              }}
            >
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: mTokens.textPrimary }}>
                Total Cash Applied :&nbsp;
                <Box component="span" sx={{ color: "#10B981" }}>
                  ${totalCash.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Box>
              </Typography>
            </Box>

            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1.2} justifyContent="flex-end" mt={2.2}>
              <Button
                type="submit"
                size="large"
                startIcon={<CheckCircleOutline />}
                removeUpperCase
                sx={{
                  ...primaryBtnSx,
                  minWidth: { sm: 200 },
                  backgroundColor: mTokens.accent,
                  boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                  "& .MuiTypography-root": { color: "#ffffff !important" },
                  "& .MuiButton-startIcon": { color: "#ffffff" },
                }}
              >
                Record Payments
              </Button>
              <Button
                color="error"
                variant="outlined"
                removeUpperCase
                startIcon={<Cancel />}
                onClick={() => navigate("/payment_list")}
                sx={{
                  ...cancelBtnSx,
                  minWidth: { sm: 140 },
                  "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                  "& .MuiButton-startIcon": { color: mTokens.error },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>

        <Dialog
          open={openCommentDialog}
          onClose={() => setOpenCommentDialog(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{ sx: paperSx(isMobile) }}
        >
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, pr: 4 }}>
              <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: mTokens.textPrimary, lineHeight: 1.3 }}>
                Add Comment
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: mTokens.textSecondary, mt: 0.15 }}>
                Add a note for this payment record.
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenCommentDialog(false)} size="small" sx={closeIconSx} aria-label="Close">
              <Close sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>
          <Box sx={bodySx}>
            <Box sx={inputFieldSx()}>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Type your comment here..."
                variant="outlined"
              />
            </Box>
          </Box>
          <Box sx={footerSx}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => {
                  if (commentIndex !== null) {
                    handleChange(commentIndex, "paymentComments", commentText);
                  }
                  setOpenCommentDialog(false);
                }}
                sx={{
                  ...primaryBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                  order: { xs: 1, sm: 2 },
                }}
              >
                Save Comment
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setOpenCommentDialog(false)}
                sx={{
                  ...cancelBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "1px solid",
                  cursor: "pointer",
                  order: { xs: 2, sm: 1 },
                }}
              >
                Cancel
              </Box>
            </Box>
          </Box>
        </Dialog>

        <OptionsPopup
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          variant="confirm"
          message="Are you sure you want to record this payment?"
          buttons={[
            { name: "Confirm", color: "primary", onClick: handleRecordPayment },
            { name: "Cancel", color: "secondary", onClick: () => setConfirmOpen(false) }
          ]}
        />
      </form>
    </Box>
  );
};

export default RecordPayment;